import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { CheckCircle2, XCircle, Loader2, Info, ExternalLink } from "lucide-react";
import { getSupabaseConfig } from "../utils/supabase/config";

export function ManualSubdomainCreator() {
  const [subdomain, setSubdomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);
  const [diagnostic, setDiagnostic] = useState<any>(null);

  const checkVercelConfig = async () => {
    try {
      const config = getSupabaseConfig();
      const response = await fetch(
        `${config.url}/functions/v1/make-server-e90c59cd/admin/vercel-diagnostic`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${config.anonKey}`,
          },
        }
      );

      const data = await response.json();
      setDiagnostic(data);
    } catch (error) {
      console.error("Failed to check Vercel config:", error);
    }
  };

  const createSubdomain = async () => {
    if (!subdomain.trim()) {
      setResult({
        success: false,
        message: "Please enter a subdomain",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const config = getSupabaseConfig();
      const response = await fetch(
        `${config.url}/functions/v1/make-server-e90c59cd/admin/create-subdomain`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${config.anonKey}`,
          },
          body: JSON.stringify({ subdomain: subdomain.trim() }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setResult({
          success: true,
          message: data.message,
          details: data,
        });
      } else {
        setResult({
          success: false,
          message: data.error || "Failed to create subdomain",
          details: data,
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if domain already exists (either from server response or error message)
  const domainAlreadyExists = result?.details?.alreadyExists || 
                               result?.details?.error?.includes("already in use") || 
                               result?.details?.details?.includes("already in use");

  // Check if this is a special subdomain
  const isSpecialSubdomain = result?.details?.isSpecialSubdomain || false;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Manual Subdomain Creator</CardTitle>
          <CardDescription>
            Create a Vercel subdomain manually if Bootstrap failed to create it
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info alert for special subdomains */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p className="mb-2"><strong>Special subdomains:</strong></p>
              <p className="text-sm">
                Use this tool to create special subdomains like <strong>demo</strong>, <strong>www</strong>, <strong>test</strong>, or <strong>staging</strong> that don't need to be linked to a company in the database.
              </p>
              <p className="text-sm mt-2">
                For regular company subdomains, make sure the company exists in the database first (create it via Bootstrap or System Admin).
              </p>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <label className="block">
              Subdomain (without .pathkit.pro)
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                placeholder="xyz"
                disabled={loading}
              />
              <Button onClick={createSubdomain} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Subdomain"
                )}
              </Button>
            </div>
            <p className="text-sm text-gray-600">
              Will create: <strong>{subdomain || "[subdomain]"}.pathkit.pro</strong>
            </p>
          </div>

          {result && (
            <>
              {/* Show error alert if domain creation failed */}
              {!domainAlreadyExists && (
                <Alert variant={result.success ? "default" : "destructive"}>
                  {result.success ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>
                    <div className="space-y-2">
                      <p>{result.message}</p>
                      {result.details && (
                        <pre className="text-xs overflow-auto">
                          {JSON.stringify(result.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Show success message if domain already exists */}
              {domainAlreadyExists && (
                <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    <div className="space-y-3">
                      <div>
                        <p className="text-green-800 dark:text-green-200">
                          <strong>Subdomain exists.</strong> The subdomain <strong>{subdomain}.pathkit.pro</strong> already exists in Vercel.
                        </p>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-3 rounded border border-green-200">
                        <p className="text-sm mb-2">
                          <strong>Next Steps:</strong>
                        </p>
                        <ol className="text-sm space-y-2 ml-4 list-decimal">
                          <li>
                            Wait 2-3 minutes for SSL certificates to provision (if you just created the subdomain)
                          </li>
                          <li>
                            Visit your subdomain:
                            <a
                              href={`https://${subdomain}.pathkit.pro`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 underline"
                            >
                              https://{subdomain}.pathkit.pro
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </li>
                          <li>
                            If you still see a 525 SSL error, wait a bit longer and refresh
                          </li>
                          <li>
                            Log in with your Bootstrap credentials
                          </li>
                        </ol>
                      </div>

                      {result.details && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-600">
                            Show technical details
                          </summary>
                          <pre className="mt-2 overflow-auto bg-gray-100 dark:bg-gray-800 p-2 rounded">
                            {JSON.stringify(result.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          <div className="pt-4 border-t">
            <Button
              variant="outline"
              onClick={checkVercelConfig}
              size="sm"
            >
              <Info className="mr-2 h-4 w-4" />
              Check Vercel Configuration
            </Button>

            {diagnostic && (
              <Alert className="mt-4">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p><strong>Vercel Configuration:</strong></p>
                    <ul className="text-sm space-y-1 ml-4">
                      <li>Configured: {diagnostic.configured ? "Yes" : "No"}</li>
                      <li>Has API Token: {diagnostic.hasApiToken ? "Yes" : "No"}</li>
                      {diagnostic.apiTokenPreview && (
                        <li>Token Preview: {diagnostic.apiTokenPreview}</li>
                      )}
                      <li>Has Project ID: {diagnostic.hasProjectId ? "Yes" : "No"}</li>
                      {diagnostic.projectId && (
                        <li>Project ID: {diagnostic.projectId}</li>
                      )}
                      <li>Has Team ID: {diagnostic.hasTeamId ? "Yes" : "No"}</li>
                      {diagnostic.teamId && (
                        <li>Team ID: {diagnostic.teamId}</li>
                      )}
                    </ul>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}